import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Modal, Button, Form } from "react-bootstrap";
import { faPaperPlane, faComments, faChevronUp, faChevronDown, faPaperclip, faFileArrowDown, faXmark } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { getWebsocketBaseURL } from "../../utils/websocketUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CalendarSvgIcon, FilterSvgIcon, GearSvgIcon, NoPhoneSvgIcon, OnOffSvgIcon, PhoneSvgIcon, PlusSvgIcon, ReservationSvgIcon, TableSvgIcon, UsersSvgIcon, XSvgIcon, UserSvgIcon, ClockSvgIcon, BellSvgIcon, BubbleTextSvgIcon, ZoneSvgIcon, PennotebookSvgIcon, ExtrasSvgIcon, ArrowUpDownIcon, ArrowUpDown, NoPhoneTwoLinesSvgIcon } from "../../icons/SvgIcons";
import { DefaultDateWeekday, DefaultDateSmallMins } from "../DefaultImports";
const CollapsibleChatContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
`;
const ChatHeader = styled.div`
  background-color: ${props => props.isNewMessage ? "#99b0ad" : "#bbbbbb"};
  color: white;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;
const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  gap: 8px; /* Espaço consistente entre os itens */
`;
const CollapseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
`;
const ChatContent = styled.div`
  display: ${props => props.isOpen ? "flex" : "none"};
  flex-direction: column;
  height: ${props => props.isOpen ? "400px" : "0"};
  transition: height 0.3s ease;
`;
const PendingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 0;
`;
const PendingChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f4f7f6;
  color: #4f5f5b;
  font-size: 12.5px;
  font-weight: 600;
`;
const PendingName = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const RemoveAttachment = styled.button`
  border: 0;
  padding: 0;
  background: none;
  color: #99b0ad;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #d94b4b;
  }
`;
const PendingNote = styled.span`
  font-size: 12.5px;
  color: #99b0ad;
`;
const PendingError = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: #d94b4b;
`;
const HiddenFileInput = styled.input`
  display: none;
`;
const AttachButton = styled.button`
  flex: 0 0 auto;
  border: 0;
  background: none;
  padding: 0 4px 0 10px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
const AttachmentImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 200px;
  border-radius: 10px;
  margin-bottom: 6px;
  cursor: pointer;
`;
const AttachmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  overflow-wrap: anywhere;
`;
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  margin: 10px;
`;
const MessageBubble = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  color: #fff;
  word-break: break-word;
  overflow-wrap: break-word;

  transform: rotate(360deg); /* Flip message back */

  &.user-message {
    background-color: #ee754c;
    margin-left: auto;
    border-bottom-right-radius: 0;
  }

  &.other-message {
    background-color: #99b0ad;
    margin-right: auto;
    border-bottom-left-radius: 0;
  }

  .message-info {
    font-size: 0.8rem;
    margin-bottom: 5px;
    text-align: right;
  }
`;
const ChatHistoryContainer = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding: 10px;
  margin-bottom: 10px;
  overscroll-behavior: contain;

  transform: rotate(360deg); /* Flip container */

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;
const TimestampCenter = styled.div`
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 5px;
  color: #6c757d;
`;
const SenderName = styled.div`
  margin-bottom: 5px;
  text-align: ${props => props.isUserMessage ? "right" : "left"};
  color: #6c757d;
  font-size: 0.9rem;
`;
const StyledTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  resize: none;
  &:focus {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;
const SendButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 15px;
  &:focus {
    outline: none;
  }
`;
function CollapsibleChat({
  reservation,
  isClient = false,
  isNewMessage,
  onToggle
}) {
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  /* Same shape as the modal chat: uploaded over HTTP the moment it is picked,
     held here until send, so the socket frame stays text only. */
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const socket = useRef(null);
  const userLogin = useSelector(state => state.userLogin);
  const {
    userInfo
  } = userLogin;
  const chatEndRef = useRef(null);
  const restaurant_info = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurant_info;
  const chatContainerRef = useRef(null);

  // New scroll handler
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Scroll to top of reversed container (visual bottom)
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };
  useEffect(() => {
    if (isOpen) {
      let sender = userInfo ? userInfo.name : reservation.full_name;
      const websocketBaseURL = getWebsocketBaseURL();
      socket.current = new WebSocket(`${websocketBaseURL}chat/init/${reservation.token}/${sender}/${isClient ? "client" : "restaurant"}/`);
      socket.current.onmessage = event => {
        const data = JSON.parse(event.data);
        if (data.type === "chat_history") {
          const sortedMessages = data.messages.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
          });
          setChatHistory(sortedMessages);
        } else {
          setChatHistory(prevHistory => {
            const updatedHistory = [...prevHistory, data];
            return updatedHistory.sort((a, b) => {
              const dateA = new Date(a.timestamp);
              const dateB = new Date(b.timestamp);
              return dateA - dateB;
            });
          });
        }
      };
      socket.current.onopen = () => {};
      socket.current.onclose = () => {};
      return () => socket.current?.close();
    }
  }, [userInfo, isOpen]);
  const sendMessage = () => {
    if (!socket.current) return;
    if (!newMessage.trim() && !attachment) return;

    socket.current.send(JSON.stringify({
      message: newMessage,
      sender: userInfo ? userInfo.name : reservation.full_name,
      client_or_restaurant: isClient ? "client" : "restaurant",
      attachment_url: attachment?.url || "",
      attachment_name: attachment?.name || "",
      attachment_type: attachment?.type || ""
    }));
    setNewMessage("");
    setAttachment(null);
  };
  const handleFilePicked = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setAttachmentError("");
    setIsUploading(true);

    try {
      const body = new FormData();
      body.append("file", file);

      const response = await fetch(`/api/reservations/chat/${reservation.token}/attachment/`, {
        method: "POST",
        body
      });

      // A 404 or a 500 arrives as an HTML page; parsing that as JSON throws
      // and would hide which failure it was behind the generic message.
      const raw = await response.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch (parseError) {
        data = null;
      }

      if (!response.ok || !data?.url) {
        setAttachmentError(data?.detail || `${t("ui.components.reservationChat.attachmentFailed")} (${response.status})`);
        return;
      }

      setAttachment(data);
    } catch (error) {
      setAttachmentError(t("ui.components.reservationChat.attachmentFailed"));
    } finally {
      setIsUploading(false);
    }
  };
  const isUserMessage = message => {
    const viewerSide = isClient ? "client" : "restaurant";
    if (["client", "restaurant"].includes(message?.client_or_restaurant)) {
      return message.client_or_restaurant === viewerSide;
    }

    // Backwards-compatible fallback for old chat rows created before the
    // sender side was stored explicitly.
    const senderName = message?.sender;
    if (isClient) return senderName === reservation.full_name;
    return senderName !== reservation.full_name;
  };
  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && typeof onToggle === "function") {
      onToggle();
    }
  };
  useEffect(() => {
    if (isOpen && chatHistory.length > 0) {
      const lastMessage = chatHistory[chatHistory.length - 1];
      const sender = userInfo ? userInfo.name : reservation.full_name;
      if (lastMessage.sender === sender) {
        // chatEndRef.current?.scrollIntoView({ behavior: "auto" });
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  }, [chatHistory, isOpen]);
  return <CollapsibleChatContainer>
      <ChatHeader isNewMessage={isNewMessage} onClick={toggleChat}>
        <ChatTitle>
          {isClient ? t("ui.screens.notifications.restaurante") : <h6 style={{
          color: "#FAF6E9",
          marginBottom: "0px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap"
        }}>
              {/* Grupo 1 - Ícone Usuário + Nome */}
              <span style={{
            display: "flex",
            alignItems: "center",
            marginRight: "7px"
          }}>
                <UserSvgIcon width={20} height={20} strokeColor={" #FAF6E9"} style={{
              marginRight: "5px"
            }} />
                {reservation.full_name.substring(0, 10) + ".."}
              </span>

              {/* Grupo 2 - Ícone Calendário + Data */}
              <span style={{
            display: "flex",
            alignItems: "center",
            marginRight: "7px"
          }}>
                <CalendarSvgIcon width={20} height={20} strokeColor={" #FAF6E9"} style={{
              marginRight: "5px"
            }} />
                <DefaultDateWeekday date={reservation.date} />
              </span>

              {/* Grupo 3 - Ícone Relógio + Hora */}
              <span style={{
            display: "flex",
            alignItems: "center",
            marginRight: "7px"
          }}>
                <ClockSvgIcon width={20} height={20} strokeColor={" #FAF6E9"} style={{
              marginRight: "5px"
            }} />
                {reservation.time}
              </span>

              {/* Grupo 4 - Ícone Usuários + Número */}
              <span style={{
            display: "flex",
            alignItems: "center"
          }}>
                <UsersSvgIcon width={30} height={30} strokeColor={" #FAF6E9"} style={{
              marginRight: "5px"
            }} />
                {reservation.number_of_people}
              </span>
            </h6>}
        </ChatTitle>
        <CollapseButton>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </CollapseButton>
      </ChatHeader>

      <ChatContent isOpen={isOpen}>
        <ChatHistoryContainer ref={chatContainerRef}>
          {chatHistory.map((msg, index) => <React.Fragment key={index}>
              <SenderName isUserMessage={isUserMessage(msg)}>
                <span className="sender">{msg.sender}</span>
              </SenderName>
              <TimestampCenter>
                <span>
                  {msg.timestamp ? <DefaultDateSmallMins date={msg.timestamp} /> : t("ui.i18nFinalCleanup.todayLower")}
                </span>
              </TimestampCenter>
              <MessageBubble className={isUserMessage(msg) ? "user-message" : "other-message"}>
                <div className="message-content">
                  {msg.attachment_url && (String(msg.attachment_type || "").startsWith("image/") ? <AttachmentImage src={msg.attachment_url} alt={msg.attachment_name || ""} onClick={() => window.open(msg.attachment_url, "_blank", "noopener")} /> : <AttachmentLink href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faFileArrowDown} />
                        <span>{msg.attachment_name || t("ui.components.reservationChat.attachment")}</span>
                      </AttachmentLink>)}

                  {msg.message && msg.message.split("\n").map((line, index) => <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>)}
                </div>
              </MessageBubble>
            </React.Fragment>)}
          <div ref={chatEndRef} />
        </ChatHistoryContainer>

        {(attachment || attachmentError || isUploading) && <PendingRow>
            {isUploading && <PendingNote>{t("ui.components.reservationChat.attachmentUploading")}</PendingNote>}

            {attachmentError && <PendingError>{attachmentError}</PendingError>}

            {attachment && !isUploading && <PendingChip>
                <FontAwesomeIcon icon={faPaperclip} />
                <PendingName>{attachment.name}</PendingName>
                <RemoveAttachment type="button" onClick={() => setAttachment(null)} aria-label="remove">
                  <FontAwesomeIcon icon={faXmark} />
                </RemoveAttachment>
              </PendingChip>}
          </PendingRow>}

        <InputContainer>
          <HiddenFileInput ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleFilePicked} />

          <AttachButton type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} title={t("ui.components.reservationChat.attachFile")} aria-label={t("ui.components.reservationChat.attachFile")}>
            <FontAwesomeIcon icon={faPaperclip} size="lg" color="#99b0ad" />
          </AttachButton>

          <StyledTextarea placeholder={t("ui.components.chat.collapsibleChat.escreverMensagem")} value={newMessage} onChange={e => setNewMessage(e.target.value)} rows={1} />
          <SendButton onClick={sendMessage}>
            <FontAwesomeIcon size="lg" icon={faPaperPlane} style={{
            color: "#9FBBC1"
          }} />
          </SendButton>
        </InputContainer>
      </ChatContent>
    </CollapsibleChatContainer>;
}
export default CollapsibleChat;
